import React, { useCallback, useEffect, useMemo } from 'react';
import { injectIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Flex } from '@atlaskit/primitives';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';

import { sharedi18n } from '../sharedi18n';
import { ContentStateColor } from '../ContentStateColor';
import { colorCodeToColorName } from '../helpers';
import { determineSecondaryColor } from '../EditorContentState/helpers';

import type { RendererContentStateComponentProps } from './types';

const i18n = defineMessages({
	lastUpdated: {
		id: 'content-state.last-updated',
		description:
			'Message in tooltip when user hovers over content state that tells them when the status was last updated',
		defaultMessage: 'Last updated',
	},
	justAMomentAgo: {
		id: 'content-state.just-a-moment',
		description:
			'Default message in tooltip when user hovers over content state that tells them when the status was last updated',
		defaultMessage: 'just a moment ago',
	},
});

const rendererContentStateWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	cursor: 'default',
	minHeight: '32px',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	whiteSpace: 'nowrap',
	marginLeft: 'space.0',
	marginRight: 'space.050',
	height: '24px',
});

const contentJumpMitigation_rendererContentStateWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	cursor: 'default',
	minHeight: 'initial',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	whiteSpace: 'nowrap',
	marginLeft: 'space.0',
	marginRight: 'space.050',
	height: '24px',
});

const classicRendererContentStateStyles = xcss({
	paddingRight: 'space.100',
	marginLeft: 'space.075',
});

const contentStateNameStyles = xcss({
	color: 'color.text',
	fontWeight: 'font.weight.medium',
});

const subtleContentStateNameStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtle',
});

const getBackgroundColor = (isSubtle: boolean, contentStatePrimaryColor?: string | null) => {
	if (isSubtle) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return '#00000000';
		} else {
			return token('utility.elevation.surface.current');
		}
	}

	return determineSecondaryColor(contentStatePrimaryColor);
};

const RendererContentStateComponentImpl = ({
	contentId,
	contentState,
	lastUpdatedInfo,
	isDraft,
	intl: { formatMessage },
}: RendererContentStateComponentProps) => {
	const updatedInfo = lastUpdatedInfo || formatMessage(i18n.justAMomentAgo);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isPageOwnerRestricted = contentState?.restrictionLevel === 'PAGE_OWNER';
	const isVerifiedContentState = isPageOwnerRestricted && contentState.name === 'Verified';

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'rendererContentState',
				attributes: {
					color: colorCodeToColorName(contentState?.color?.toUpperCase()),
					restrictionLevel: contentState?.restrictionLevel?.toUpperCase(),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentState, contentId]);

	const handleMouseEnter = useCallback(() => {
		if (!isDraft) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'hovered',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'rendererContentStateButtonHovered',
					source: 'rendererContentStateButton',
					attributes: {
						contentId,
					},
				},
			}).fire();
		}
	}, [contentId, isDraft, createAnalyticsEvent]);

	const isLivePage = useIsCurrentPageLive();

	const contentStatePrimaryColor = contentState?.color;

	const contentStateBackgroundColor = getBackgroundColor(
		isLivePage || fg('cc_page_experiences_new_editor_byline'),
		contentStatePrimaryColor,
	);

	const getTooltipContent = useMemo(() => {
		if (isPageOwnerRestricted) {
			return formatMessage(sharedi18n.verifiedRestricted);
		} else if (isLivePage || fg('cc_page_experiences_new_editor_byline')) {
			return formatMessage(sharedi18n.pageStatus);
		}

		return `${formatMessage(i18n.lastUpdated)}: ${updatedInfo}`;
	}, [isPageOwnerRestricted, isLivePage, updatedInfo, formatMessage]);

	const getContentStateColorAppearance = () => {
		// The verified content state for the live page byline and new renderer byline experiment is special in that due to having a checkmark in it, a larger styling is required over the smaller subtle style
		if ((isLivePage || fg('cc_page_experiences_new_editor_byline')) && isVerifiedContentState) {
			return 'contentStateButton';
		} else if (isLivePage || fg('cc_page_experiences_new_editor_byline')) {
			return 'subtleRendererContentState';
		}

		return 'rendererContentState';
	};

	return (
		<Flex>
			<Tooltip content={getTooltipContent} position="bottom">
				<Box
					as="li"
					xcss={[
						fg('live_pages_content_jump_mitigation')
							? contentJumpMitigation_rendererContentStateWrapperStyles
							: rendererContentStateWrapperStyles,
						!(isLivePage || fg('cc_page_experiences_new_editor_byline')) &&
							classicRendererContentStateStyles,
					]}
					onMouseEnter={handleMouseEnter}
					testId="renderer-content-state-wrapper"
					style={{
						backgroundColor: contentStateBackgroundColor,
					}}
				>
					<ContentStateColor
						appearance={getContentStateColorAppearance()}
						dataTestId="renderer-content-state-color"
						color={contentState?.color}
						restrictionLevel={contentState?.restrictionLevel}
					/>
					<Box
						xcss={[
							contentStateNameStyles,
							(isLivePage || fg('cc_page_experiences_new_editor_byline')) &&
								subtleContentStateNameStyles,
						]}
					>
						{contentState?.name}
					</Box>
				</Box>
			</Tooltip>
		</Flex>
	);
};

export const RendererContentStateComponent = injectIntl(RendererContentStateComponentImpl);
